import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import './SmartListView.scss';
import FlatList from './FlatList';
import SectionList from './SectionList';
import { useAutoLoadMore } from './useAutoLoadMore';
import { useBidirectionalInfiniteScroll } from './useBidirectionalInfiniteScroll';

// ─── SmartListView ──────────────────────────────────────────────────────────
//
// Routing layer that sits above FlatList / SectionList.
// Responsibilities:
//   • Pick the right list component (sections → SectionList, else → FlatList)
//   • Wire up bidirectional infinite scroll + auto-load-more hooks
//   • Expose a stable imperative ref API to callers
//
// It deliberately owns NO rendering logic — all of that lives in
// FlatList / SectionList / useVirtualization.

const SmartListBase = (props, ref) => {
   const {
      id,
      data = [],
      sections,
      renderItem,
      renderSectionHeader,
      keyExtractor = (item, index) => index,
      onLoadTop,
      onLoadBottom,
      hasMoreTop = false,
      hasMoreBottom = true,
      offset = 5,
      debounceMs = 300,
      horizontal = false,
      ListHeaderComponent,
      ListFooterComponent,
      ItemSeparatorComponent,
      SectionSeparatorComponent,
      ListEmptyComponent,
      itemHeight = 60,
      initialScrollIndex,
      initialScrollKey,
      initialScrollBlock,
      ListOverlayComponent,
      enableVirtualization = true,
      overscan = 5,
      getItemHeight,
      virtualizationThreshold = 50,
      enableAutoLoadMore = true,
      loading = false,
      inverted = false,
      scrollToBottom,
      LoadingTopComponent,
      LoadingBottomComponent,
   } = props;

   const containerRef = useRef(null);
   const listRef = useRef(null);

   // ─── Snapshot wrappers ──────────────────────────────────────────────────
   // Take a scroll snapshot before each load so the list can restore position
   // once new items are prepended / appended.

   const handleLoadTop = useMemo(() => {
      if (!onLoadTop) return null;
      return async () => {
         listRef.current?.takeScrollSnapshot('top');
         return onLoadTop();
      };
   }, [onLoadTop]);

   const handleLoadBottom = useMemo(() => {
      if (!onLoadBottom) return null;
      return async () => {
         listRef.current?.takeScrollSnapshot('bottom');
         return onLoadBottom();
      };
   }, [onLoadBottom]);

   // ─── Infinite scroll ────────────────────────────────────────────────────

   const { loadingTop, loadingBottom } = useBidirectionalInfiniteScroll({
      containerRef,
      onLoadTop: handleLoadTop,
      onLoadBottom: handleLoadBottom,
      hasMoreTop,
      hasMoreBottom,
      offset,
      debounceMs,
      inverted,
   });

   // Shim so useAutoLoadMore can call getScrollContainer() on the listRef shape
   const autoLoadListRef = useMemo(() => ({ current: { getScrollContainer: () => containerRef.current } }), []);

   useAutoLoadMore({
      listRef: enableAutoLoadMore && onLoadBottom && hasMoreBottom ? autoLoadListRef : { current: null },
      data,
      hasMore: enableAutoLoadMore ? hasMoreBottom : false,
      loadMore: enableAutoLoadMore && onLoadBottom ? onLoadBottom : () => {},
      loading: loading || loadingBottom || loadingTop,
      checkDelay: 100,
      debounceMs,
   });

   // ─── Imperative API ─────────────────────────────────────────────────────

   useImperativeHandle(ref, () => ({
      scrollToOffset: ({ offset: scrollOffset, animated = true }) => {
         containerRef.current?.scrollTo({
            top: scrollOffset,
            behavior: animated ? 'smooth' : 'auto',
         });
      },
      scrollToEnd: ({ animated = true } = {}) => {
         if (inverted) {
            containerRef.current?.scrollTo({ top: 0, behavior: animated ? 'smooth' : 'auto' });
         } else {
            containerRef.current?.scrollTo({
               top: containerRef.current.scrollHeight,
               behavior: animated ? 'smooth' : 'auto',
            });
         }
      },
      jumpToBottom: options => listRef.current?.jumpToBottom(options),
      scrollToIndex: ({ index, animated = true, block = 'center' }) =>
         listRef.current?.scrollToIndex?.(index, { smooth: animated, block }),
      scrollToKey: (key, options = {}) => listRef.current?.scrollToKey?.(key, options),
      scrollToItem: ({ itemKey, animated = true, block = 'center' }) =>
         listRef.current?.scrollToKey?.(itemKey, { smooth: animated, block }),
      flashScrollIndicators: () => {
         containerRef.current?.classList.add('flash-scroll');
         setTimeout(() => containerRef.current?.classList.remove('flash-scroll'), 1000);
      },
      getScrollContainer: () => containerRef.current,
   }));

   // ─── Shared props for both list variants ────────────────────────────────

   const sharedListProps = {
      ref: listRef,
      containerRef,
      renderItem,
      keyExtractor,
      itemHeight,
      overscan,
      getItemHeight,
      enableVirtualization,
      virtualizationThreshold,
      ItemSeparatorComponent,
      ListEmptyComponent,
      initialScrollKey,
      initialScrollIndex,
      initialScrollBlock,
      inverted,
   };

   // ─── List component ─────────────────────────────────────────────────────
   // SectionList when sections is provided, FlatList otherwise.
   // Both share the same ref API so the imperative handle above works for both.

   const listContent = sections ? (
      <SectionList
         {...sharedListProps}
         sections={sections}
         renderSectionHeader={renderSectionHeader}
         SectionSeparatorComponent={SectionSeparatorComponent}
      />
   ) : (
      <FlatList {...sharedListProps} items={data} />
   );

   // ─── Container class names ──────────────────────────────────────────────

   const isSectionVirtualized =
      sections &&
      enableVirtualization &&
      sections.reduce((acc, s) => acc + (s.data?.length ?? 0), 0) >= virtualizationThreshold;
   const isFlatVirtualized = !sections && enableVirtualization && data.length >= virtualizationThreshold;
   const isVirtualized = isSectionVirtualized || isFlatVirtualized;

   return (
      <div
         ref={containerRef}
         id={id || 'smart-list-view'}
         className={[
            'smart-list-view',
            'ct-scroll',
            inverted && 'ct-flex ct-fcolRev',
            horizontal && 'horizontal ct-flex ct-frow',
            isVirtualized && 'virtualized',
         ]
            .filter(Boolean)
            .join(' ')}
      >
         {ListHeaderComponent}
         {LoadingTopComponent}
         {listContent}
         {LoadingBottomComponent}
         {ListFooterComponent}
         {ListOverlayComponent}
         {scrollToBottom?.()}
      </div>
   );
};

const SmartListView = forwardRef(SmartListBase);

SmartListBase.propTypes = {
   id: PropTypes.string,
   // FlatList data
   data: PropTypes.array,
   // SectionList data — mutually exclusive with `data`
   sections: PropTypes.arrayOf(
      PropTypes.shape({
         title: PropTypes.string,
         data: PropTypes.array.isRequired,
         renderItem: PropTypes.func,
      }),
   ),
   renderItem: PropTypes.func.isRequired,
   renderSectionHeader: PropTypes.func,
   keyExtractor: PropTypes.func,
   // Infinite scroll
   onLoadTop: PropTypes.func,
   onLoadBottom: PropTypes.func,
   hasMoreTop: PropTypes.bool,
   hasMoreBottom: PropTypes.bool,
   offset: PropTypes.number,
   debounceMs: PropTypes.number,
   // Layout
   horizontal: PropTypes.bool,
   inverted: PropTypes.bool,
   // Slot components
   ListHeaderComponent: PropTypes.node,
   ListFooterComponent: PropTypes.node,
   ItemSeparatorComponent: PropTypes.node,
   SectionSeparatorComponent: PropTypes.node,
   ListEmptyComponent: PropTypes.node,
   ListOverlayComponent: PropTypes.node,
   LoadingTopComponent: PropTypes.node,
   LoadingBottomComponent: PropTypes.node,
   scrollToBottom: PropTypes.func,
   // Virtualization
   itemHeight: PropTypes.number,
   overscan: PropTypes.number,
   getItemHeight: PropTypes.func,
   enableVirtualization: PropTypes.bool,
   virtualizationThreshold: PropTypes.number,
   // Initial scroll
   initialScrollIndex: PropTypes.number,
   initialScrollKey: PropTypes.string,
   initialScrollBlock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   // Auto-load
   enableAutoLoadMore: PropTypes.bool,
   loading: PropTypes.bool,
};

export default SmartListView;
